/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { randomString } from "common/utils/strings"
import {
  CURRENT_TAB_KEY,
  MONITOR_CONFIG_INTERVAL_IN_MILLISECOND,
  MAX_ACTIVE_TAB_LEASE_TIME,
  MONITOR_HERMES_CONNECTION_INTERVAL_IN_MILLISECOND,
  MAX_OPENED_TAB_VERIFICATION_TIME,
  LAST_UPDATE_TAB_INTERVAL_IN_MILLISECOND,
} from "constants/sessions_storage"
import { isNull, isUndefined } from "lodash"
import { getOrderManagerNotificationConfig } from "order-manager/utils/storage_config_helper"
import { storageHelper } from "utils/storage"
import { HermesStore, HermesConnectionStatus } from "connections/hermes"
import { OrderNotification } from "order-manager/types/models/notification"
import { Order } from "order-manager/types/models/order"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { NotificationMethod } from "order-manager/constants/tracking"

const generateNotificationData = (
  order: Order,
  merchantID: string,
): OrderNotification => ({
  merchantID,
  orderID: order.orderID,
  shortOrderID: order.displayID,
  timestamp: Math.floor(Date.now() / 1000),
  source: NotificationMethod.Fallback,
})

export const setupBeforeUnloadListener = () => {
  window.addEventListener("beforeunload", () => {
    const currentTabID = sessionStorage.getItem(CURRENT_TAB_KEY) || ""

    const oMnotifConfig = getOrderManagerNotificationConfig()
    if (oMnotifConfig.activeTabID === currentTabID) {
      oMnotifConfig.activeTabID = ""
      oMnotifConfig.enableFallback = false
      oMnotifConfig.enableHermes = false
    }

    if (!isUndefined(oMnotifConfig.openedTabs)) {
      delete oMnotifConfig.openedTabs[currentTabID]
    }

    storageHelper.setOrderManagerNotificationConfig(oMnotifConfig)
  })
}

const countOpenedTab = (openedTabs: Record<string, number>): number => {
  let result = 0
  const currentTime = Math.floor(Date.now() / 1000)

  Object.entries(openedTabs).forEach(([tabID]) => {
    // every tab will regularly update their own lastUpdateTime using updateOpenedTabsRegularly function
    if (currentTime - openedTabs[tabID] <= MAX_OPENED_TAB_VERIFICATION_TIME) {
      result += 1
    }
  })

  return result
}

export const setupNotificationConfig = () => {
  const currentTabID = sessionStorage.getItem(CURRENT_TAB_KEY) || randomString() // get tab id from session otherwise generate new one.
  const oMnotifConfig = getOrderManagerNotificationConfig()
  sessionStorage.setItem(CURRENT_TAB_KEY, currentTabID)

  /*
    handle EDGE CASE issue when chrome is force close by any method, it caused beforeunload not triggered.
    activeTabID will not set to empty and no new tab can claim it forever (it means no notification).
  */
  const currentTime = Math.floor(Date.now() / 1000)
  const isPassedStorageTime =
    currentTime - oMnotifConfig.lastUpdate > MAX_ACTIVE_TAB_LEASE_TIME

  if (oMnotifConfig.activeTabID === "" || isPassedStorageTime) {
    oMnotifConfig.activeTabID = currentTabID
    oMnotifConfig.lastUpdate = currentTime
  }

  if (isUndefined(oMnotifConfig.openedTabs)) {
    oMnotifConfig.openedTabs = {}
  }

  oMnotifConfig.openedTabs[currentTabID] = currentTime
  storageHelper.setOrderManagerNotificationConfig(oMnotifConfig)

  // Detect multiple tab opened
  if (
    oMnotifConfig.activeTabID !== "" &&
    oMnotifConfig.activeTabID !== currentTabID &&
    !isPassedStorageTime
  ) {
    TroyWebTracker.sendEvent({
      category: EventCategory.pageOpen,
      action: EventAction.multipleTab,
      labels: {
        OPENED_TAB_COUNT: countOpenedTab(oMnotifConfig.openedTabs),
      },
    })
  }
}

// monitor empty active tab id in local storage, claim it if empty
export const monitorActiveTab = (hermesObject: HermesStore) => {
  // don't run interval if current tab is active tab id
  if (
    getOrderManagerNotificationConfig().activeTabID ===
    sessionStorage.getItem(CURRENT_TAB_KEY)
  ) {
    return
  }

  setInterval(() => {
    const currentTabID = sessionStorage.getItem(CURRENT_TAB_KEY)
    const oMnotifConfig = getOrderManagerNotificationConfig()

    // claim the active tab id and trigger hermes connection
    if (oMnotifConfig.activeTabID === "" && !isNull(currentTabID)) {
      oMnotifConfig.activeTabID = currentTabID
      oMnotifConfig.lastUpdate = Math.floor(Date.now() / 1000)
      storageHelper.setOrderManagerNotificationConfig(oMnotifConfig)

      if (
        hermesObject.connectionStatus === HermesConnectionStatus.Disconnected &&
        !oMnotifConfig.enableFallback
      ) {
        hermesObject.connect(true)
      }
    }
  }, MONITOR_CONFIG_INTERVAL_IN_MILLISECOND)
}

export const monitorHermesConnection = (hermesObject: HermesStore) => {
  setInterval(() => {
    const currentTabID = sessionStorage.getItem(CURRENT_TAB_KEY)
    const oMnotifConfig = getOrderManagerNotificationConfig()

    if (
      currentTabID === oMnotifConfig.activeTabID &&
      hermesObject.connectionStatus === HermesConnectionStatus.Disconnected &&
      oMnotifConfig.enableFallback
    ) {
      hermesObject.connect(true)
    }
  }, MONITOR_HERMES_CONNECTION_INTERVAL_IN_MILLISECOND)
}

export const updateOpenedTabsRegularly = () => {
  setInterval(() => {
    const currentTabID = sessionStorage.getItem(CURRENT_TAB_KEY) || ""
    const oMnotifConfig = getOrderManagerNotificationConfig()

    if (isUndefined(oMnotifConfig.openedTabs)) {
      oMnotifConfig.openedTabs = {}
    }

    oMnotifConfig.openedTabs[currentTabID] = Math.floor(Date.now() / 1000)
    storageHelper.setOrderManagerNotificationConfig(oMnotifConfig)
  }, LAST_UPDATE_TAB_INTERVAL_IN_MILLISECOND)
}

export const isEnableFallbackNotification = (): boolean => {
  const currentTabID = sessionStorage.getItem(CURRENT_TAB_KEY)
  const oMnotifConfig = getOrderManagerNotificationConfig()

  return (
    oMnotifConfig.activeTabID === currentTabID && oMnotifConfig.enableFallback
  )
}

// Comparing current list with the new data to know whether a new order exist or not.
// Only return first order and trigger notification sound once.
// It will be annoying if we trigger notification more than one time.
export const checkNewOrder = (
  currentData: Order[] | undefined,
  newData: Order[],
  merchantID: string,
): [boolean, OrderNotification] => {
  const notif: OrderNotification = {}

  if (newData.length === 0) {
    return [false, notif]
  }

  // prevent unnecessary loop if current list is undefined(first load) and newData has value
  if (isUndefined(currentData) && newData.length >= 1) {
    return [false, notif]
  }

  for (let i = 0; i < newData.length; i += 1) {
    const found =
      currentData?.find(
        currentOrder => currentOrder.orderID === newData[i].orderID,
      ) || false
    if (!found) {
      return [true, generateNotificationData(newData[i], merchantID)]
    }
  }

  return [false, notif]
}
